import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { PlusCircle, Trash2, FileText, Settings as SettingsIcon, Layers, Activity, User as UserIcon, Database } from 'lucide-react';
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import LazyLoad from "@/components/LazyLoad";

const SettingsTabSkeleton = () => (
  <div className="space-y-4 animate-pulse">
    <Skeleton className="h-12 w-full max-w-md mb-4" />
    <Skeleton className="h-64 w-full" />
    <Skeleton className="h-36 w-full" />
  </div>
);

const Settings = () => {
  const [competitors, setCompetitors] = useState(['HDFC Bank', 'ICICI Bank', 'SBI']);
  const [newCompetitor, setNewCompetitor] = useState('');
  const [branches, setBranches] = useState([
    { id: 1, city: 'Mumbai', state: 'Maharashtra', type: 'Physical' },
    { id: 2, city: 'Delhi', state: 'Delhi', type: 'Physical' },
    { id: 3, city: 'Bangalore', state: 'Karnataka', type: 'Digital' },
  ]);
  const [isLoading, setIsLoading] = useState(true);

  // Optimize with faster initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 50); // Quick initial load
    return () => clearTimeout(timer);
  }, []);

  const handleAddCompetitor = () => {
    if (newCompetitor) {
      setCompetitors([...competitors, newCompetitor]);
      setNewCompetitor('');
      toast.success("Competitor added");
    }
  };

  const handleRemoveCompetitor = (index: number) => {
    const updatedCompetitors = [...competitors];
    updatedCompetitors.splice(index, 1);
    setCompetitors(updatedCompetitors);
    toast.success("Competitor removed");
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground">
            Manage your organization settings and configurations.
          </p>
        </div>
        <Button onClick={() => toast.success("Settings saved")}>
          <SettingsIcon className="mr-2 h-4 w-4" /> Save Changes
        </Button>
      </div>

      {isLoading ? (
        <SettingsTabSkeleton />
      ) : (
        <Tabs defaultValue="org-details" className="space-y-4">
          <TabsList className="grid grid-cols-1 lg:grid-cols-3 h-auto">
            <TabsTrigger value="org-details">Organization</TabsTrigger>
            <TabsTrigger value="user-mgmt">Users</TabsTrigger>
            <TabsTrigger value="logs">System Logs</TabsTrigger>
          </TabsList>

          {/* Organization Details Tab */}
          <TabsContent value="org-details" className="space-y-4">
            <LazyLoad delay={50}>
              <Card>
                <CardHeader>
                  <CardTitle>Organization Details</CardTitle>
                  <CardDescription>Configure your organization information</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="org-name">Organization Name</Label>
                      <Input id="org-name" placeholder="Enter organization name" defaultValue="Rench Financial" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="entity-type">Entity Type</Label>
                      <Select defaultValue="nbfc">
                        <SelectTrigger id="entity-type">
                          <SelectValue placeholder="Select entity type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="bank">Bank</SelectItem>
                          <SelectItem value="nbfc">NBFC</SelectItem>
                          <SelectItem value="mfi">MFI</SelectItem>
                          <SelectItem value="hfc">HFC</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Separator />
                  
                  <div className="space-y-4">
                    <Label>Products</Label>
                    <div className="flex flex-wrap gap-2">
                      {["Housing Loans", "SME Loans", "Personal Loans", "Supply Chain"].map((product) => (
                        <Badge key={product} variant="outline" className="py-1.5 cursor-pointer hover:bg-accent">
                          {product}
                          <Trash2 className="ml-2 h-3 w-3 text-muted-foreground hover:text-destructive" />
                        </Badge>
                      ))}
                      <Badge variant="outline" className="bg-muted/50 py-1.5 cursor-pointer hover:bg-accent">
                        <PlusCircle className="mr-1 h-3 w-3" /> Add Product
                      </Badge>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Label>Branches</Label>
                      <Button variant="outline" size="sm" onClick={() => toast.success("Branch added")}>
                        <PlusCircle className="mr-2 h-3 w-3" /> Add Branch
                      </Button>
                    </div>
                    <div className="rounded-md border overflow-hidden">
                      <table className="min-w-full divide-y divide-border">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="px-4 py-2 text-left text-sm font-medium">City</th>
                            <th className="px-4 py-2 text-left text-sm font-medium">State</th>
                            <th className="px-4 py-2 text-left text-sm font-medium">Type</th>
                            <th className="px-4 py-2 text-left text-sm font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {branches.map((branch) => (
                            <tr key={branch.id}>
                              <td className="px-4 py-2 text-sm">{branch.city}</td>
                              <td className="px-4 py-2 text-sm">{branch.state}</td>
                              <td className="px-4 py-2 text-sm">
                                <Badge variant={branch.type === 'Digital' ? 'secondary' : 'outline'}>
                                  {branch.type}
                                </Badge>
                              </td>
                              <td className="px-4 py-2 text-sm">
                                <Button variant="ghost" size="sm">
                                  <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-4">
                    <Label>Competitors Mapping</Label>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {competitors.map((competitor, index) => (
                        <Badge key={index} variant="outline" className="py-1.5">
                          {competitor}
                          <button 
                            onClick={() => handleRemoveCompetitor(index)}
                            className="ml-2 h-3 w-3 text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input 
                        placeholder="Add competitor"
                        value={newCompetitor}
                        onChange={(e) => setNewCompetitor(e.target.value)}
                        className="max-w-sm"
                      />
                      <Button onClick={handleAddCompetitor}>Add</Button>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          toast.success("AI scan for competitors initiated");
                          setTimeout(() => {
                            setCompetitors([...competitors, 'Axis Bank', 'Bajaj Finserv']);
                            toast.success("New competitors identified");
                          }, 2000);
                        }}
                      >
                        Auto-scan with AI
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </LazyLoad>
          </TabsContent>

          {/* User Management Tab */}
          <TabsContent value="user-mgmt" className="space-y-4">
            <LazyLoad delay={100}>
              <Card>
                <CardHeader>
                  <CardTitle>User Management</CardTitle>
                  <CardDescription>Configure users, roles and permissions</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-medium">Users</h3>
                      <Button size="sm" onClick={() => toast.success("Add user dialog would open")}>
                        <PlusCircle className="mr-2 h-3 w-3" /> Add User
                      </Button>
                    </div>

                    <div className="rounded-md border overflow-hidden">
                      <table className="min-w-full divide-y divide-border">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="px-4 py-2 text-left text-sm font-medium">Name</th>
                            <th className="px-4 py-2 text-left text-sm font-medium">Email</th>
                            <th className="px-4 py-2 text-left text-sm font-medium">Function</th>
                            <th className="px-4 py-2 text-left text-sm font-medium">Branch</th>
                            <th className="px-4 py-2 text-left text-sm font-medium">Hierarchy</th>
                            <th className="px-4 py-2 text-left text-sm font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {[
                            { name: "Rajesh Kumar", email: "rajesh@example.com", function: "Finance", branch: "Mumbai", hierarchy: "Manager" },
                            { name: "Priya Singh", email: "priya@example.com", function: "Compliance", branch: "Delhi", hierarchy: "Senior Officer" },
                            { name: "Amit Patel", email: "amit@example.com", function: "Treasury", branch: "Bangalore", hierarchy: "Analyst" },
                          ].map((user, i) => (
                            <tr key={i}>
                              <td className="px-4 py-2 text-sm">{user.name}</td>
                              <td className="px-4 py-2 text-sm">{user.email}</td>
                              <td className="px-4 py-2 text-sm">{user.function}</td>
                              <td className="px-4 py-2 text-sm">{user.branch}</td>
                              <td className="px-4 py-2 text-sm">{user.hierarchy}</td>
                              <td className="px-4 py-2 text-sm">
                                <div className="flex space-x-2">
                                  <Button variant="ghost" size="sm" onClick={() => toast.success(`Editing ${user.name}`)}>
                                    Edit
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => toast.success(`Deactivating ${user.name}`)}>
                                    Deactivate
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <Separator />

                    <div className="space-y-4">
                      <h3 className="text-lg font-medium">Functions & Roles</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Function</Label>
                          <div className="flex flex-col gap-2">
                            {["Finance", "Treasury", "Compliance", "Legal", "Operations"].map((func) => (
                              <Badge key={func} variant="outline" className="justify-between py-1.5">
                                {func}
                                <div className="flex">
                                  <Button variant="ghost" size="sm" className="h-4 w-4 p-0">
                                    <PlusCircle className="h-3 w-3" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-4 w-4 p-0">
                                    <Trash2 className="h-3 w-3 text-destructive" />
                                  </Button>
                                </div>
                              </Badge>
                            ))}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>Branch Tagging</Label>
                          <div className="flex flex-col gap-2">
                            {["Mumbai", "Delhi", "Bangalore", "Chennai", "Digital"].map((branch) => (
                              <Badge key={branch} variant="outline" className="justify-between py-1.5">
                                {branch}
                                <div className="flex">
                                  <Button variant="ghost" size="sm" className="h-4 w-4 p-0">
                                    <PlusCircle className="h-3 w-3" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-4 w-4 p-0">
                                    <Trash2 className="h-3 w-3 text-destructive" />
                                  </Button>
                                </div>
                              </Badge>
                            ))}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>Hierarchy Levels</Label>
                          <div className="flex flex-col gap-2">
                            {["C-Suite", "Director", "Manager", "Senior Officer", "Officer", "Analyst"].map((level) => (
                              <Badge key={level} variant="outline" className="justify-between py-1.5">
                                {level}
                                <div className="flex">
                                  <Button variant="ghost" size="sm" className="h-4 w-4 p-0">
                                    <PlusCircle className="h-3 w-3" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-4 w-4 p-0">
                                    <Trash2 className="h-3 w-3 text-destructive" />
                                  </Button>
                                </div>
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </LazyLoad>
          </TabsContent>

          {/* System Logs Tab */}
          <TabsContent value="logs" className="space-y-4">
            <LazyLoad delay={150}>
              <Card>
                <CardHeader>
                  <CardTitle>System Logs</CardTitle>
                  <CardDescription>Monitor all activities and changes in the system</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-medium">Activity Logs</h3>
                      <div className="flex gap-2">
                        <Select defaultValue="all">
                          <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="Filter by type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Activities</SelectItem>
                            <SelectItem value="user">User Actions</SelectItem>
                            <SelectItem value="ai">AI Actions</SelectItem>
                            <SelectItem value="system">System Events</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="outline">Export Logs</Button>
                      </div>
                    </div>

                    <div className="rounded-md border overflow-hidden">
                      <table className="min-w-full divide-y divide-border">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="px-4 py-2 text-left text-sm font-medium">Timestamp</th>
                            <th className="px-4 py-2 text-left text-sm font-medium">Actor</th>
                            <th className="px-4 py-2 text-left text-sm font-medium">Type</th>
                            <th className="px-4 py-2 text-left text-sm font-medium">Action</th>
                            <th className="px-4 py-2 text-left text-sm font-medium">Details</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border bg-white">
                          {[
                            { timestamp: "2025-04-30 14:32:15", actor: "Rajesh Kumar", type: "user", action: "Document Upload", details: "Uploaded bank statement for HDFC Bank" },
                            { timestamp: "2025-04-30 14:28:03", actor: "AI Assistant", type: "ai", action: "Data Processing", details: "Processed bank statement and extracted 24 transactions" },
                            { timestamp: "2025-04-30 14:15:42", actor: "System", type: "system", action: "Integration", details: "Successfully connected to SAP ERP" },
                            { timestamp: "2025-04-30 13:52:18", actor: "Priya Singh", type: "user", action: "User Management", details: "Added new user: Amit Patel" },
                            { timestamp: "2025-04-30 13:45:29", actor: "AI Assistant", type: "ai", action: "Alert", details: "Detected potential compliance issue in document #RF-2025-042" },
                            { timestamp: "2025-04-30 13:30:05", actor: "System", type: "system", action: "Backup", details: "System database backup completed successfully" },
                          ].map((log, i) => (
                            <tr key={i} className={log.type === "ai" ? "bg-blue-50" : log.type === "system" ? "bg-gray-50" : ""}>
                              <td className="px-4 py-2 text-sm">{log.timestamp}</td>
                              <td className="px-4 py-2 text-sm">
                                <div className="flex items-center">
                                  {log.type === "ai" ? (
                                    <Activity className="h-3 w-3 mr-1 text-blue-500" />
                                  ) : log.type === "system" ? (
                                    <Database className="h-3 w-3 mr-1 text-gray-500" />
                                  ) : (
                                    <UserIcon className="h-3 w-3 mr-1" />
                                  )}
                                  {log.actor}
                                </div>
                              </td>
                              <td className="px-4 py-2 text-sm">
                                <Badge variant={log.type === "ai" ? "secondary" : log.type === "system" ? "outline" : "default"}>
                                  {log.type === "ai" ? "AI" : log.type === "system" ? "System" : "User"}
                                </Badge>
                              </td>
                              <td className="px-4 py-2 text-sm">{log.action}</td>
                              <td className="px-4 py-2 text-sm">{log.details}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </LazyLoad>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default Settings;
